// Palette of draggable field types for the Invoice Builder.
// Each entry describes what shows up in the sidebar, a default box size/style
// when dropped on the canvas, and how to render its value from real invoice
// data (falling back to sampleInvoiceData below for on-canvas preview).
//
// This mirrors the data shape the existing print pipeline already passes to
// Invoice1/Invoice2/Invoice3.../KOT components (see src/components/Invoices/),
// so a field's `path` should resolve against that same object shape once this
// builder is wired into real printing later.

// 1mm at the same px/mm ratio the app already uses at print time
// (src/pages/POSMain.jsx: 80 * 3.7795275591 for 80mm paper).
export const MM_TO_PX = 3.7795275591;
export const CANVAS_WIDTH_MM = 80;
export const CANVAS_WIDTH_PX = Math.round(CANVAS_WIDTH_MM * MM_TO_PX);

function getPath(obj, path) {
  return path
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

export const sampleInvoiceData = {
  general: {
    restaurant_name: "Imdady Restaurant",
    address: "King Fahd Road, Riyadh, Saudi Arabia",
    phone: "+966 50 123 4567",
    tax_reg_no: "300012345600003",
    whatsapp: "+966 50 765 4321",
    tax_method: "Inclusive",
    email: "info@imdadyrms.co",
    days: "Saturday-Thursday",
    start_time: "09:00 AM",
    end_time: "12:00 AM",
    hours_display: "09:00 AM - 12:00 AM",
  },
  outletData: {
    name: "Main Branch",
    address: "Olaya Branch, Riyadh, Saudi Arabia",
    phone: "+966 50 111 2222",
  },
  data: {
    invoice_no: "INV-000482",
    reference: "429945702",
    date_time: "21-07-2026 04:32 PM",
    order_type: "Dine In",
    table: "Table 4",
    cashier: "Ahmed Ali",
    customer: { name: "Walk-in Customer" },
    order_details: [
      { name: "Chicken Shawarma", qty: 2, price: 18.0, vat: 2.7, total: 38.7 },
      { name: "French Fries", qty: 1, price: 10.0, vat: 1.5, total: 11.5 },
      { name: "Soft Drink", qty: 2, price: 6.0, vat: 1.8, total: 13.8 },
    ],
    sub_total: 46.0,
    discount: 0,
    vat_total: 6.0,
    total_payable: 64.0,
    payment_method: "Cash",
    qr_image_base64:
      "data:image/svg+xml;base64," +
      btoa(
        '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="#fff"/><rect x="10" y="10" width="80" height="80" fill="none" stroke="#000" stroke-width="4"/><text x="50" y="55" font-size="10" text-anchor="middle">QR</text></svg>'
      ),
  },
  invoice: {
    footer1: "Thank you for visiting!",
    footer2: "www.imdadyrms.co",
  },
};

export const FIELD_TYPES = {
  LOGO: "logo",
  TEXT: "text", // static, user-typed text
  DATA: "data", // bound to a data path, single line
  ITEMS_TABLE: "items_table",
  TOTALS: "totals",
  QR: "qr",
  DIVIDER: "divider",
};

// `path` resolves against the object built from sampleInvoiceData's shape
// (general / outletData / data / invoice) when previewing on the canvas.
export const FIELD_PALETTE = [
  {
    key: "logo",
    type: FIELD_TYPES.LOGO,
    label: "Logo",
    defaultSize: { width: 120, height: 60 },
    defaultStyle: { fontSize: 12, align: "center", bold: false },
  },
  {
    key: "restaurant_name",
    type: FIELD_TYPES.DATA,
    label: "Restaurant Name",
    path: "general.restaurant_name",
    defaultSize: { width: 220, height: 26 },
    defaultStyle: { fontSize: 16, align: "center", bold: true },
  },
  {
    key: "address",
    type: FIELD_TYPES.DATA,
    label: "Address (Outlet)",
    path: "outletData.address",
    defaultSize: { width: 260, height: 30 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "phone",
    type: FIELD_TYPES.DATA,
    label: "Phone (Outlet)",
    path: "outletData.phone",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "tax_reg_no",
    type: FIELD_TYPES.DATA,
    label: "Tax Registration No",
    path: "general.tax_reg_no",
    defaultSize: { width: 240, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "outlet_name",
    type: FIELD_TYPES.DATA,
    label: "Outlet / Branch Name",
    path: "outletData.name",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "center", bold: false },
  },
  {
    key: "general_address",
    type: FIELD_TYPES.DATA,
    label: "Address (Restaurant Setting)",
    path: "general.address",
    defaultSize: { width: 260, height: 30 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "general_phone",
    type: FIELD_TYPES.DATA,
    label: "Phone (Restaurant Setting)",
    path: "general.phone",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "whatsapp_number",
    type: FIELD_TYPES.DATA,
    label: "Whatsapp Number",
    path: "general.whatsapp",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "email",
    type: FIELD_TYPES.DATA,
    label: "Email",
    path: "general.email",
    defaultSize: { width: 220, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "tax_method",
    type: FIELD_TYPES.DATA,
    label: "Tax Method",
    path: "general.tax_method",
    defaultSize: { width: 160, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "operating_days",
    type: FIELD_TYPES.DATA,
    label: "Days",
    path: "general.days",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "operating_hours",
    type: FIELD_TYPES.DATA,
    label: "Hours",
    path: "general.hours_display",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "invoice_no",
    type: FIELD_TYPES.DATA,
    label: "Invoice No",
    path: "data.invoice_no",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "reference",
    type: FIELD_TYPES.DATA,
    label: "Reference No",
    path: "data.reference",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "date_time",
    type: FIELD_TYPES.DATA,
    label: "Date & Time",
    path: "data.date_time",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "order_type",
    type: FIELD_TYPES.DATA,
    label: "Order Type",
    path: "data.order_type",
    defaultSize: { width: 160, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "table",
    type: FIELD_TYPES.DATA,
    label: "Table",
    path: "data.table",
    defaultSize: { width: 160, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "cashier",
    type: FIELD_TYPES.DATA,
    label: "Cashier",
    path: "data.cashier",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "customer_name",
    type: FIELD_TYPES.DATA,
    label: "Customer Name",
    path: "data.customer.name",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "items_table",
    type: FIELD_TYPES.ITEMS_TABLE,
    label: "Items Table",
    defaultSize: { width: CANVAS_WIDTH_PX - 20, height: 140 },
    defaultStyle: { fontSize: 11, align: "left", bold: false },
  },
  {
    key: "totals",
    type: FIELD_TYPES.TOTALS,
    label: "Totals (Subtotal/Discount/VAT/Total)",
    defaultSize: { width: 220, height: 90 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "payment_method",
    type: FIELD_TYPES.DATA,
    label: "Payment Method",
    path: "data.payment_method",
    defaultSize: { width: 200, height: 20 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
  },
  {
    key: "qr_code",
    type: FIELD_TYPES.QR,
    label: "QR Code (ZATCA)",
    path: "data.qr_image_base64",
    defaultSize: { width: 100, height: 100 },
    defaultStyle: { fontSize: 12, align: "center", bold: false },
  },
  {
    key: "footer1",
    type: FIELD_TYPES.DATA,
    label: "Footer Line 1",
    path: "invoice.footer1",
    defaultSize: { width: 260, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "footer2",
    type: FIELD_TYPES.DATA,
    label: "Footer Line 2",
    path: "invoice.footer2",
    defaultSize: { width: 260, height: 20 },
    defaultStyle: { fontSize: 11, align: "center", bold: false },
  },
  {
    key: "divider",
    type: FIELD_TYPES.DIVIDER,
    label: "Divider Line",
    defaultSize: { width: CANVAS_WIDTH_PX - 20, height: 20 },
    defaultStyle: {},
  },
  {
    key: "custom_text",
    type: FIELD_TYPES.TEXT,
    label: "Custom Text",
    defaultSize: { width: 200, height: 24 },
    defaultStyle: { fontSize: 12, align: "left", bold: false },
    defaultText: "Custom text",
  },
];

export function resolveFieldValue(field, previewData = sampleInvoiceData) {
  if (field.type === FIELD_TYPES.TEXT) return field.text ?? "";
  if (field.type === FIELD_TYPES.DATA) {
    const value = getPath(previewData, field.path);
    return value == null || value === "" ? `{${field.label}}` : String(value);
  }
  return "";
}

export function getPaletteEntry(key) {
  return FIELD_PALETTE.find((f) => f.key === key);
}
